import type { TableProps, TabsProps } from "antd";
import { Button, DatePicker, Input, Tabs } from "antd";
import dayjs, { Dayjs } from "dayjs";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CSVIcon } from "../../components/Common/Icon";
import { LicenseState } from "../../enum/lisence";
import searchIcon from "../../images/search_icon.png";
import dateIcon from "../../images/from-to-icon.svg";
import { DataType } from "../../models/interface/license.interface";
import { api } from "../../utils/api";
import { PAGE, PAGE_SIZE } from "../../utils/constants/common";
import Tab2 from "./LisenceMangement/Tab2";
import Tab3 from "./LisenceMangement/Tab3";
import UpdatePaymentModal from "./LisenceMangement/UpdatePaymentModal";

const AdminLisenceManagementPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [list, setList] = React.useState<DataType[]>([]);
  const [total, setTotal] = React.useState(0);
  const [listId, setListId] = React.useState<number[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [state, setState] = useState<string | null>(null);
  const [shipping, setShipping] = useState<string | null>(null);

  const page = Number(searchParams.get("page") || PAGE);
  const pageSize = Number(searchParams.get("pageSize") || PAGE_SIZE);
  const tab = searchParams.get("tab") || LicenseState.REQUESTING;

  const searchTerm = searchParams.get("search") || "";
  const startDate = searchParams.get("startDate") || "";
  const endDate = searchParams.get("endDate") || "";

  const [searchInput, setSearchInput] = React.useState(searchTerm);
  const [startDateInput, setStartDateInput] = useState<Dayjs | null>(
    startDate ? dayjs(startDate) : null,
  );

  const [endDateInput, setEndDateInput] = useState<Dayjs | null>(
    endDate ? dayjs(endDate) : null,
  );

  const dateFormat = "YYYY-MM-DD";

  const getData = useCallback(async () => {
    const res = await api.get(`api/licenses`, {
      params: {
        page: page,
        pageSize: pageSize,
        state: tab,
        search: searchTerm,
        startDate: startDate,
        endDate: endDate,
      },
    });
    setList(res.data.items);
    setTotal(res.data.total);
  }, [page, pageSize, tab, searchTerm, startDate, endDate]);

  useEffect(() => {
    setSearchInput(searchTerm);
    setStartDateInput(startDate ? dayjs(startDate) : null);
    setEndDateInput(endDate ? dayjs(endDate) : null);
  }, [searchTerm, startDate, endDate]);

  useEffect(() => {
    getData();
  }, [getData]);

  const onSearch = () => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (searchInput) {
        params.set("search", searchInput);
      } else {
        params.delete("search");
      }
      if (startDateInput) {
        params.set("startDate", startDateInput.format(dateFormat));
      } else {
        params.delete("startDate");
      }
      if (endDateInput) {
        params.set("endDate", endDateInput.format(dateFormat));
      } else {
        params.delete("endDate");
      }
      params.set("page", "1");
      return params;
    });
  };

  const onChange = (key: string) => {
    setSearchParams({ tab: key });
    setSearchInput("");
    setStartDateInput(null);
    setEndDateInput(null);
  };

  const rowSelection: TableProps<DataType>["rowSelection"] = useMemo(
    () => ({
      onChange: (_, selectedRows: DataType[]) => {
        setListId(selectedRows.map((item) => item.id));
      },
      selectedRowKeys: listId,
      hideSelectAll: true,
    }),
    [listId],
  );

  const openModalEditState = () => {
    setIsModalOpen(true);
  };

  const handleOk = async () => {
    setIsModalOpen(false);
    const data = {
      ids: listId,
      newState: state,
      newShippingStatus: shipping,
    };
    await api.put(`api/licenses/payment-status`, data);
    getData();
    setListId([]);
  };

  const handleCancel = () => {
    setState(null);
    setShipping(null);
    setIsModalOpen(false);
  };

  const items: TabsProps["items"] = useMemo<TabsProps["items"]>(
    () => [
      {
        key: LicenseState.REQUESTING,
        label: "申請中",
        children: <Tab2 key={tab} data={list} total={total} />,
      },
      {
        key: LicenseState.ACTIVATED,
        label: "準備完了",
        children: (
          <Tab3
            key={tab}
            data={list}
            total={total}
            rowSelection={rowSelection}
          />
        ),
      },
      {
        key: LicenseState.REVOKED,
        label: "廃棄",
        children: null,
      },
    ],
    [list, total, rowSelection, tab],
  );

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex justify-between items-center h-[46px]">
        <div className="flex gap-3 h-full">
          <Input
            maxLength={255}
            placeholder="カメラSN・ハードウェアSN・代理店名で検索"
            className="base-input h-11 w-[424px]"
            suffix={<img src={searchIcon} className="h-6" alt="search icon" />}
            onChange={(e) => {
              setSearchInput(e.target.value);
            }}
            value={searchInput}
          />

          {tab === LicenseState.ACTIVATED && (
            <div className="h-full flex items-center gap-2">
              <DatePicker
                value={startDateInput}
                onChange={(date) => setStartDateInput(date)}
                maxDate={dayjs(endDateInput, dateFormat)}
                className="h-full"
              />
              <img src={dateIcon} alt="" />
              <DatePicker
                value={endDateInput}
                onChange={(date) => setEndDateInput(date)}
                minDate={dayjs(startDateInput, dateFormat)}
                className="h-full"
              />
            </div>
          )}
          <Button
            onClick={() => onSearch()}
            className="h-[46px] w-[52px] base-button"
          >
            検索
          </Button>
        </div>
        <div className="flex items-center gap-2 justify-end flex-1">
          <Button
            onClick={() => openModalEditState()}
            className="h-11 w-[150px] base-button"
            disabled={!(listId.length > 0)}
          >
            ステータス更新
          </Button>
          <Button
            className="h-11 w-[137px] text-[#0077C8] text-xl font-medium border-[#0077C8]"
            icon={<CSVIcon />}
          >
            CSV出力
          </Button>
        </div>
      </div>
      <Tabs
        activeKey={tab}
        items={items}
        onChange={onChange}
        className="tabs-custom"
      />
      <UpdatePaymentModal
        isModalOpen={isModalOpen}
        handleOk={handleOk}
        handleCancel={handleCancel}
        state={state}
        setState={setState}
        shipping={shipping}
        setShipping={setShipping}
      />
    </div>
  );
};

export default AdminLisenceManagementPage;
